import { getItemUniqueKey, getPackageId } from 'checkout/lib/utils/accommodation/cart'
import { getOptInBundleInclusionsFromRate } from 'checkout/lib/utils/accommodation/bundles'
import type { IntlShape } from 'react-intl'

function occupancyFromSession(session: App.AccommodationEnquirySession): App.Occupants {
  const { occupancy } = session.data
  return {
    adults: occupancy.adults,
    children: occupancy.children,
    teenagers: occupancy.teenagers,
    infants: occupancy.infants,
    childrenAge: occupancy.childrenAges,
  }
}

function emptyTotals(): App.Checkout.ItemViewTotals {
  return {
    price: 0,
    memberPrice: 0,
    value: 0,
    memberValue: 0,
    surcharge: 0,
    extraGuestSurcharge: 0,
    taxesAndFees: 0,
  }
}

export function getAccommodationV2ItemView(
  intl: IntlShape,
  item: App.Checkout.AccommodationV2Item,
  sessionRecord: App.CheckoutState['accommodationSessions'][string] | undefined,
  property?: App.Property.Property,
): App.WithDataStatus<App.Checkout.AccommodationV2AccommodationItemView> {
  const session = sessionRecord?.data
  const rate = sessionRecord?.rate
  const outcome = sessionRecord?.outcome
  const room = property?.rooms[item.roomId] ?? (session ? property?.rooms[session.data.room.roomId] : undefined)
  const occupancy = session ? occupancyFromSession(session) : item.occupancy
  const soldOut = outcome === 'unavailable' || (!rate && outcome != null)
  const image = room?.images[0] ?? property?.images[0] ?? {}
  const primaryTitle = room?.name || rate?.name || ''
  const propertyFees = session?.data.pricing.propertyFees ?? rate?.totals.propertyFees ?? 0
  // svc-order charges `session.data.pricing.sellTotal`. Property fees are stored separately
  // and shown as due-at-property via `otherFees`, not added into `price`.
  const price = session?.data.pricing.sellTotal ?? rate?.totals.sellingPrice ?? 0

  const view: App.Checkout.AccommodationV2AccommodationItemView = {
    kind: 'accommodationV2',
    packageId: getPackageId(item),
    uniqueKey: getItemUniqueKey(item),
    item,
    image,
    primaryTitle,
    occupancy,
    propertyId: item.propertyId,
    rate,
    inclusions: rate?.inclusions,
    bonusInclusions: rate?.bonusInclusions,
    bundleInclusions: item.bundleOptin ? undefined : rate?.bundleInclusions,
    optInBundleInclusions: item.bundleOptin
      ? getOptInBundleInclusionsFromRate(rate, item)
      : undefined,
    removeLabel: intl.formatMessage({
      id: 'WmkzQGWSf9s2KSRQdBJ4',
      defaultMessage: 'Remove',
      description: 'Remove button label for accommodationV2 checkout item',
    }),
    soldOut,
    enquiryError: sessionRecord?.error,
    loyalty: {},
    title: primaryTitle,
    totals: session || rate ? {
      price,
      // ponytail: LuxPlus totals blocked on HP-2041 (customer availability hardcodes isLuxPlusUser=false)
      memberPrice: 0,
      value: rate?.totals.valuedUpToPrice ?? price,
      memberValue: 0,
      surcharge: 0,
      extraGuestSurcharge: 0,
      taxesAndFees: rate?.totals.taxesAndFees ?? 0,
      otherFees: propertyFees ? { propertyFees, extraGuestSurcharge: 0 } : undefined,
    } : emptyTotals(),
  }

  const hasRequiredData = !!session && !!property && !sessionRecord?.error && (outcome === 'unavailable' || !!rate)

  return { hasRequiredData, data: view }
}
